/**
 * Hook qui résout le slug d'URL (`/clients/paja`) en infos client.
 * En mode démo : retourne le client Paja mocké directement.
 * En mode prod : fetch depuis public.clients via Supabase.
 */
import { useQuery } from '@tanstack/react-query';
import { useUser, useAuth, IS_DEMO_MODE } from '@/lib/auth';
import { useParams } from 'react-router-dom';
import { supabasePublic } from '@/lib/supabase';

export type ClientRow = {
  id: string;
  slug: string;
  company_name: string;
  contact_name: string | null;
  contact_email: string | null;
  niche: string | null;
  status: string;
  health_status: string | null;
  plan_tier: string | null;
  mrr_eur: number;
  started_at: string | null;
  metadata: Record<string, unknown>;
  /** True si le coach a déjà rempli son form de config post-paiement. */
  has_config: boolean;
};

const MOCK_CLIENT: ClientRow = {
  id: '44444444-4444-4444-4444-444444444444',
  slug: 'marie-dupont',
  company_name: 'Coach Marie Dupont',
  contact_name: 'Marie Dupont',
  contact_email: 'sebastian.stephen.dominguez@gmail.com',
  niche: 'coach-business',
  status: 'active',
  health_status: 'green',
  plan_tier: 'growth',
  mrr_eur: 1590,
  started_at: '2026-04-20',
  metadata: {
    is_test_account: true,
    city: 'Lyon',
    founded_year: 2022,
    annual_ca_target_eur: 280000,
  },
  has_config: true, // Marie est déjà configurée pour la démo
};

export function useClient() {
  const { slug } = useParams<{ slug: string }>();
  const { user, isLoaded } = useUser();
  const { getToken } = useAuth();

  const isAdmin = user?.publicMetadata?.role === 'admin';
  const allowedClientIds = user?.publicMetadata?.allowed_clients ?? [];

  return useQuery({
    queryKey: ['client', slug],
    enabled: !!slug && isLoaded,
    queryFn: async (): Promise<{ client: ClientRow; canAccess: boolean }> => {
      if (IS_DEMO_MODE) {
        if (slug !== 'marie-dupont' && slug !== 'paja') {
          throw new Error(`Client not found: ${slug}`);
        }
        return { client: MOCK_CLIENT, canAccess: true };
      }

      const supa = supabasePublic(() => getToken());
      // Lookup direct sur la colonne `slug` (UNIQUE depuis migration 2026-04-30).
      const { data, error } = await supa
        .from('clients')
        .select('*')
        .eq('slug', slug)
        .single();

      if (error || !data) {
        throw new Error(`Client not found: ${slug}`);
      }
      const clientRaw = data as Omit<ClientRow, 'has_config'>;

      // Détecte si le coach a déjà rempli son form de config post-paiement.
      // Va lire client_onboarding.config_payload via metadata.client_onboarding_id.
      const onboardingId = (clientRaw.metadata ?? {})['client_onboarding_id'];
      let hasConfig = false;
      if (typeof onboardingId === 'string') {
        const { data: ob } = await supa
          .from('client_onboarding')
          .select('config_payload')
          .eq('id', onboardingId)
          .maybeSingle();
        const cfg = ob && (ob as { config_payload: unknown }).config_payload;
        hasConfig = cfg !== null && cfg !== undefined;
      }

      const client: ClientRow = { ...clientRaw, has_config: hasConfig };
      const canAccess = isAdmin || allowedClientIds.includes(client.id);
      return { client, canAccess };
    },
  });
}
